import { useMemo } from 'react';

import { isEnumEqual } from '@utils/isEnumEqual';

import { SYSTEM_ROLES } from '@constants';
import { useAppSelector } from '@hooks';

interface IUserRole {
  isUserSuperAdmin?: boolean;
  hasAddPermission?: boolean;
  hasEditPermission?: boolean;
  hasReadPermission?: boolean;
  hasCancelPermission?: boolean;
  hasExportPermission?: boolean;
  hasCreatePermission?: boolean;
}

type PermissionFlags = Pick<
  IUserRole,
  | 'hasAddPermission'
  | 'hasEditPermission'
  | 'hasReadPermission'
  | 'hasCancelPermission'
  | 'hasCreatePermission'
  | 'hasExportPermission'
>;

const PERMISSION_FLAG_RULES: ReadonlyArray<{
  prefix: string;
  key: keyof PermissionFlags;
}> = [
  { prefix: 'edit:', key: 'hasEditPermission' },
  { prefix: 'export:', key: 'hasExportPermission' },
  { prefix: 'read:', key: 'hasReadPermission' },
  { prefix: 'cancel:', key: 'hasCancelPermission' },
  { prefix: 'create:', key: 'hasCreatePermission' },
  { prefix: 'add:', key: 'hasAddPermission' },
];

const emptyPermissionFlags = (): Record<keyof PermissionFlags, boolean> => ({
  hasAddPermission: false,
  hasEditPermission: false,
  hasReadPermission: false,
  hasCancelPermission: false,
  hasCreatePermission: false,
  hasExportPermission: false,
});

const collectPermissionFlagsForModule = (
  permissions: readonly string[],
  module: SYSTEM_ROLES.PermissionModules
): Record<keyof PermissionFlags, boolean> => {
  const flags = emptyPermissionFlags();

  for (const permission of permissions) {
    if (permission?.split(':')?.[1] !== module) {
      continue;
    }

    for (const { key, prefix } of PERMISSION_FLAG_RULES) {
      if (permission.startsWith(prefix)) {
        flags[key] = true;
      }
    }
  }

  return flags;
};

export const usePermissions = (
  module: SYSTEM_ROLES.PermissionModules
): IUserRole => {
  const userType = useAppSelector((state) => state.auth.userConfig.userType);
  const permissions = useAppSelector(
    (state) => state.auth.userConfig.permissions
  );

  const isUserSuperAdmin = useMemo(
    () => isEnumEqual(userType, SYSTEM_ROLES.ROLE.SUPER_ADMIN),
    [userType]
  );

  const {
    hasAddPermission,
    hasReadPermission,
    hasEditPermission,
    hasExportPermission,
    hasCancelPermission,
    hasCreatePermission,
  } = useMemo(
    () => collectPermissionFlagsForModule(permissions, module),
    [module, permissions]
  );

  return {
    isUserSuperAdmin,
    hasAddPermission,
    hasReadPermission,
    hasEditPermission,
    hasExportPermission,
    hasCancelPermission,
    hasCreatePermission,
  };
};
